import { Helmet } from 'react-helmet-async';
import siteMetadata from './data/siteMetadata';

/**
 * Page metadata for the whole site.
 *
 * The existing `PageSEO` / `TagSEO` / `BlogSEO` API is unchanged so that every
 * page already calling them keeps working. What changed underneath:
 *
 *  - `react-helmet-async`, because `react-helmet` uses lifecycle methods React 18
 *    warns about and drops updates under concurrent rendering.
 *  - Indexability is decided by the URL rather than hardcoded to `index, follow`.
 *    Admin screens, the dashboard, the cart and the course player were all
 *    inviting Google to index private pages.
 *  - Canonicals ignore the query string, so `?utm_source=facebook` no longer
 *    presents every advertisement as a separate duplicate page.
 */

/**
 * Paths that must never be indexed: private application surfaces, and anything
 * behind authentication. Matched as prefixes against the pathname.
 */
const PRIVATE_PATH_PREFIXES = [
  '/admin',
  '/profile',
  '/cart',
  '/auth',
  '/onboarding',
  '/password',
  '/course/', // the enrolled player — /course-details/:slug is the public page
  '/partners/dashboard', // a partner's own figures. /partners itself is public.
  '/r/', // a referral hop, not a destination — it redirects within two seconds
];

const isPrivatePath = (path: string): boolean =>
  PRIVATE_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(prefix));

const getPath = (): string => (typeof window !== 'undefined' ? window.location.pathname : '/');

/** Canonical URLs are path-only: no UTM parameters, no trailing-slash duplicates. */
const canonicalFor = (path: string): string => {
  const trimmed = path !== '/' && path.endsWith('/') ? path.slice(0, -1) : path;
  return `${siteMetadata.siteUrl}${trimmed}`;
};

const absolute = (url: string): string =>
  url.startsWith('http') ? url : `${siteMetadata.siteUrl}${url.startsWith('/') ? '' : '/'}${url}`;

/** Twitter wants a handle, but siteMetadata stores the full profile URL. */
const twitterHandle = (() => {
  const match = siteMetadata.twitter.match(/x\.com\/([^/?]+)/i);
  return match ? `@${match[1]}` : '@mindalitix';
})();

interface CommonSEOProps {
  title: string;
  description: string;
  ogType: string;
  ogImage: string | { '@type': string; url: string }[];
  twImage: string;
  canonicalUrl?: string;
  /** Force a value; otherwise the path decides. */
  noindex?: boolean;
}

export const CommonSEO = ({
  title,
  description,
  ogType,
  ogImage,
  twImage,
  canonicalUrl,
  noindex,
}: CommonSEOProps) => {
  const path = getPath();
  const canonical = canonicalUrl ?? canonicalFor(path);
  const blocked = noindex ?? isPrivatePath(path);

  return (
    <Helmet>
      <title>{title}</title>
      <meta
        name="robots"
        content={
          blocked
            ? 'noindex, nofollow'
            : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
        }
      />
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={siteMetadata.headerTitle} />
      <meta property="og:locale" content={siteMetadata.locale} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {Array.isArray(ogImage) ? (
        ogImage.map(({ url }) => <meta property="og:image" content={absolute(url)} key={url} />)
      ) : (
        <meta property="og:image" content={absolute(ogImage)} key={ogImage} />
      )}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absolute(twImage)} />
    </Helmet>
  );
};

interface PageSEOProps {
  title: string;
  description: string;
  noindex?: boolean;
  canonicalUrl?: string;
  /** Absolute or site-relative image for the social preview. */
  image?: string;
}

export const PageSEO = ({ title, description, noindex, canonicalUrl, image }: PageSEOProps) => {
  const imageUrl = absolute(image ?? siteMetadata.socialBanner);
  return (
    <CommonSEO
      title={title}
      description={description}
      ogType="website"
      ogImage={imageUrl}
      twImage={imageUrl}
      canonicalUrl={canonicalUrl}
      noindex={noindex}
    />
  );
};

export const TagSEO = ({ title, description }: PageSEOProps) => {
  const path = getPath();
  const imageUrl = absolute(siteMetadata.socialBanner);
  return (
    <>
      <CommonSEO title={title} description={description} ogType="website" ogImage={imageUrl} twImage={imageUrl} />
      <Helmet>
        <link
          rel="alternate"
          type="application/rss+xml"
          title={`${description} - RSS feed`}
          href={`${siteMetadata.siteUrl}${path}/feed.xml`}
        />
      </Helmet>
    </>
  );
};

interface BlogSEOProps {
  title: string;
  summary: string;
  date: string;
  lastmod?: string;
  url: string;
  images?: string | string[];
  canonicalUrl?: string;
  authorDetails?: { name: string; twitter?: string }[];
}

export const BlogSEO = ({
  title,
  summary,
  date,
  lastmod,
  url,
  images = [],
  canonicalUrl,
  authorDetails,
}: BlogSEOProps) => {
  const publishedAt = new Date(date).toISOString();
  const modifiedAt = new Date(lastmod || date).toISOString();
  const imagesArr = Array.isArray(images) ? images : typeof images === 'string' ? [images] : [];
  const featuredImages = (imagesArr.length > 0 ? imagesArr : [siteMetadata.socialBanner]).map((img) => ({
    '@type': 'ImageObject',
    url: absolute(img),
  }));

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    headline: title,
    image: featuredImages,
    datePublished: publishedAt,
    dateModified: modifiedAt,
    author: authorDetails?.map((author) => ({ '@type': 'Person', name: author.name })) ?? {
      '@type': 'Organization',
      name: siteMetadata.headerTitle,
    },
    publisher: {
      '@type': 'Organization',
      name: siteMetadata.headerTitle,
      logo: { '@type': 'ImageObject', url: absolute(siteMetadata.siteLogo) },
    },
    description: summary,
  };

  return (
    <>
      <CommonSEO
        title={title}
        description={summary}
        ogType="article"
        ogImage={featuredImages}
        twImage={featuredImages[0].url}
        canonicalUrl={canonicalUrl}
      />
      <Helmet>
        <meta property="article:published_time" content={publishedAt} />
        <meta property="article:modified_time" content={modifiedAt} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
    </>
  );
};

/**
 * Site-wide identity, rendered once from the app shell. Gives Google the
 * organisation, the verified social profiles it already has, and the sitelinks
 * search box pointing at the real course search.
 */
export const SiteStructuredData = () => {
  const profiles = [
    siteMetadata.twitter,
    siteMetadata.facebook,
    siteMetadata.instagram,
    siteMetadata.linkedin,
    siteMetadata.github,
  ].filter(Boolean);

  const graph = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${siteMetadata.siteUrl}/#organization`,
        name: siteMetadata.headerTitle,
        url: siteMetadata.siteUrl,
        logo: { '@type': 'ImageObject', url: absolute(siteMetadata.siteLogo) },
        email: siteMetadata.email,
        sameAs: profiles,
      },
      {
        '@type': 'WebSite',
        '@id': `${siteMetadata.siteUrl}/#website`,
        url: siteMetadata.siteUrl,
        name: siteMetadata.headerTitle,
        description: siteMetadata.description,
        publisher: { '@id': `${siteMetadata.siteUrl}/#organization` },
        inLanguage: siteMetadata.language,
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${siteMetadata.siteUrl}/courses?search={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
    ],
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(graph)}</script>
    </Helmet>
  );
};

export interface CourseStructuredDataProps {
  name: string;
  description: string;
  url: string;
  image?: string;
  instructorName?: string;
  /** Only pass what the record actually has — never fabricate ratings or counts. */
  price?: number;
  currency?: string;
  datePublished?: string;
}

/**
 * `Course` markup for a public course page. Deliberately conservative: it emits
 * only fields the course record genuinely holds. No aggregateRating, no review
 * count, no fabricated enrolment numbers.
 */
export const CourseStructuredData = ({
  name,
  description,
  url,
  image,
  instructorName,
  price,
  currency = 'NGN',
  datePublished,
}: CourseStructuredDataProps) => {
  const data: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name,
    description,
    url: absolute(url),
    provider: {
      '@type': 'Organization',
      name: siteMetadata.headerTitle,
      sameAs: siteMetadata.siteUrl,
    },
  };

  if (image) data.image = absolute(image);
  if (datePublished) data.datePublished = datePublished;
  if (instructorName) data.instructor = { '@type': 'Person', name: instructorName };
  if (typeof price === 'number') {
    data.offers = {
      '@type': 'Offer',
      price: String(price),
      priceCurrency: currency,
      category: price === 0 ? 'Free' : 'Paid',
      url: absolute(url),
      availability: 'https://schema.org/InStock',
    };
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

export interface FaqStructuredDataProps {
  items: { question: string; answer: string }[];
}

/** Only ever pass the FAQ entries that are visibly on the page. */
export const FaqStructuredData = ({ items }: FaqStructuredDataProps) => {
  if (items.length === 0) return null;
  const data = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};
